import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '../supabaseClient';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';
import { isPersistableUuid } from '../utils/programContentSlideOwner';
import { layoutFromDbRow, type ProgramContentSlidePageRow } from './programContentSlideService';
import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';

/**
 * 공개보기 QR 세션 기본 유효시간(분). 교실에서 하루 종일 재사용해도 만료되지 않도록
 * 8시간(480분)으로 설정한다. DB 트리거(`presentation_public_sessions_before_insert`)가
 * 최대 24시간(1440분)까지만 허용하므로 그 안에서 조정 가능.
 */
export const PRESENTATION_PUBLIC_SESSION_TTL_MINUTES = 480;
const PUBLIC_PRESENTATION_CACHE_PREFIX = 'cp:public-presentation-cache:v1:';

function formatPresentationShareRpcError(
  message: string,
  status?: number,
  code?: string
): string {
  const msg = String(message ?? '');
  if (/no api key found/i.test(msg)) {
    return 'Supabase API 키가 요청에 포함되지 않았습니다. 개발 서버를 재시작한 뒤 페이지를 새로고침해 주세요.';
  }
  if (/not authenticated/i.test(msg)) {
    return '로그인이 필요합니다. 다시 로그인한 뒤 시도해 주세요.';
  }
  if (/not a school member/i.test(msg)) {
    return '이 워크스페이스 슬라이드를 공개할 권한이 없습니다.';
  }
  if (/workspace_id required|school_id required/i.test(msg)) {
    return '워크스페이스 정보가 올바르지 않습니다. 저장한 뒤 다시 시도해 주세요.';
  }
  if (
    code === 'PGRST202' ||
    /could not find the function|PGRST202/i.test(msg)
  ) {
    return '공개보기 DB 함수를 API에서 찾지 못했습니다. Supabase 대시보드에서 스키마를 새로고침한 뒤 다시 시도해 주세요.';
  }
  if (status === 404) {
    return `공개보기 요청이 실패했습니다(404). ${msg}`;
  }
  return msg || '공개보기 QR을 만들지 못했습니다.';
}

async function postSupabaseRpc<T>(functionName: string, body: Record<string, unknown>, bearerToken: string): Promise<T> {
  const baseUrl = SUPABASE_URL.replace(/\/$/, '');
  const anonKey = SUPABASE_ANON_KEY.trim();
  if (!baseUrl || !anonKey) {
    throw new Error('Supabase 환경변수가 설정되지 않았습니다.');
  }

  const res = await fetch(`${baseUrl}/rest/v1/rpc/${functionName}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const rawText = await res.text();
  let parsed: unknown = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const errBody = parsed as { message?: string; code?: string } | null;
    const message = errBody?.message || rawText || `HTTP ${res.status}`;
    if (process.env.NODE_ENV === 'development') {
      console.error('[presentationPublicShare]', functionName, res.status, errBody ?? rawText, {
        bodyKeys: Object.keys(body),
        bodyPreview: JSON.stringify(body).slice(0, 240),
      });
    }
    throw new Error(formatPresentationShareRpcError(message, res.status, errBody?.code));
  }

  return parsed as T;
}

async function getAuthBearerToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw new Error(error.message);
  const accessToken = data.session?.access_token?.trim();
  if (!accessToken) {
    throw new Error(formatPresentationShareRpcError('not authenticated'));
  }
  return accessToken;
}

function parseCreateSessionResult(data: unknown): { token: string; expiresAt: string } | null {
  if (!data) return null;
  if (Array.isArray(data)) {
    const row = data[0] as { token?: string; expires_at?: string } | undefined;
    if (!row) return null;
    return { token: String(row.token ?? '').trim(), expiresAt: String(row.expires_at ?? '').trim() };
  }
  if (typeof data === 'object') {
    const row = data as { token?: string; expires_at?: string };
    const token = String(row.token ?? '').trim();
    const expiresAt = String(row.expires_at ?? '').trim();
    if (token) return { token, expiresAt };
  }
  return null;
}

export interface PublicPresentationPayload {
  meta: ProgramContentSlidePayloadV1['meta'];
  extraPageCount: number;
  expiresAt: string;
  layouts: Record<number, ProgramSlideLayoutV1>;
}

interface PublicPresentationCachePayloadV1 {
  v: 1;
  token: string;
  signature: string;
  expiresAt: string;
  payload: PublicPresentationPayload;
}

function publicPresentationStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function publicPresentationCacheKey(token: string): string {
  return `${PUBLIC_PRESENTATION_CACHE_PREFIX}${token.trim()}`;
}

function readPublicPresentationCache(token: string): PublicPresentationCachePayloadV1 | null {
  const keyToken = token.trim();
  const storage = publicPresentationStorage();
  if (!keyToken || !storage) return null;
  try {
    const raw = storage.getItem(publicPresentationCacheKey(keyToken));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PublicPresentationCachePayloadV1;
    if (!parsed || parsed.v !== 1) return null;
    if ((parsed.token ?? '').trim() !== keyToken) return null;
    if (!parsed.payload || typeof parsed.payload !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePublicPresentationCache(
  token: string,
  signature: string,
  payload: PublicPresentationPayload
): void {
  const keyToken = token.trim();
  const sig = signature.trim();
  const storage = publicPresentationStorage();
  if (!keyToken || !sig || !storage) return;
  try {
    const record: PublicPresentationCachePayloadV1 = {
      v: 1,
      token: keyToken,
      signature: sig,
      expiresAt: payload.expiresAt,
      payload,
    };
    storage.setItem(publicPresentationCacheKey(keyToken), JSON.stringify(record));
  } catch {
    /* quota 등은 무시 */
  }
}

function removePublicPresentationCache(token: string): void {
  const keyToken = token.trim();
  const storage = publicPresentationStorage();
  if (!keyToken || !storage) return;
  try {
    storage.removeItem(publicPresentationCacheKey(keyToken));
  } catch {
    /* ignore */
  }
}

export function buildPublicPresentationUrl(token: string): string {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const path = `${basePath}/p/${encodeURIComponent(token)}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }
  return path;
}

const activePublicSessionMemoryCache = new Map<string, { token: string; expiresAt: string }>();

function activePublicSessionCacheKey(workspaceId: string, presentationRefId: string): string {
  return `${workspaceId}:${presentationRefId}`;
}

function readActivePublicSessionMemoryCache(
  workspaceId: string,
  presentationRefId: string
): { token: string; expiresAt: string } | null {
  const cached = activePublicSessionMemoryCache.get(activePublicSessionCacheKey(workspaceId, presentationRefId));
  if (!cached) return null;
  if (new Date(cached.expiresAt).getTime() <= Date.now()) {
    activePublicSessionMemoryCache.delete(activePublicSessionCacheKey(workspaceId, presentationRefId));
    return null;
  }
  return cached;
}

function writeActivePublicSessionMemoryCache(
  workspaceId: string,
  presentationRefId: string,
  session: { token: string; expiresAt: string }
): void {
  activePublicSessionMemoryCache.set(activePublicSessionCacheKey(workspaceId, presentationRefId), session);
}

function parseActiveSessionRpcResult(data: unknown): { token: string; expiresAt: string } | null {
  if (!data || typeof data !== 'object') return null;
  const row = data as { token?: string; expires_at?: string };
  const token = String(row.token ?? '').trim();
  const expiresAt = String(row.expires_at ?? '').trim();
  if (!token || !expiresAt) return null;
  if (new Date(expiresAt).getTime() <= Date.now()) return null;
  return { token, expiresAt };
}

async function findActivePresentationPublicSessionViaRpc(
  workspaceId: string,
  presentationRefId: string
): Promise<{ token: string; expiresAt: string } | null> {
  try {
    const accessToken = await getAuthBearerToken();
    const data = await postSupabaseRpc<{ token?: string; expires_at?: string } | null>(
      'find_active_presentation_public_session',
      {
        p_workspace_id: workspaceId,
        p_presentation_ref_id: presentationRefId,
      },
      accessToken
    );
    return parseActiveSessionRpcResult(data);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e ?? '');
    if (!/find_active_presentation_public_session|could not find the function|PGRST202/i.test(msg)) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[presentationPublicShare] find active session rpc', e);
      }
    }
    return null;
  }
}

async function findActivePresentationPublicSessionViaTable(
  workspaceId: string,
  presentationRefId: string
): Promise<{ token: string; expiresAt: string } | null> {
  const { data, error } = await supabase
    .from('presentation_public_sessions')
    .select('token, expires_at')
    .eq('workspace_id', workspaceId)
    .eq('presentation_ref_id', presentationRefId)
    .is('revoked_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[presentationPublicShare] find active session table', error);
    }
    return null;
  }

  return parseActiveSessionRpcResult(data);
}

async function findActivePresentationPublicSession(
  workspaceId: string,
  presentationRefId: string
): Promise<{ token: string; expiresAt: string } | null> {
  const cached = readActivePublicSessionMemoryCache(workspaceId, presentationRefId);
  if (cached) return cached;

  const fromRpc = await findActivePresentationPublicSessionViaRpc(workspaceId, presentationRefId);
  if (fromRpc) {
    writeActivePublicSessionMemoryCache(workspaceId, presentationRefId, fromRpc);
    return fromRpc;
  }

  const fromTable = await findActivePresentationPublicSessionViaTable(workspaceId, presentationRefId);
  if (fromTable) {
    writeActivePublicSessionMemoryCache(workspaceId, presentationRefId, fromTable);
    return fromTable;
  }

  return null;
}

export async function createPresentationPublicSession(params: {
  workspaceId: string;
  presentationRefId: string;
  meta: ProgramContentSlidePayloadV1['meta'];
  extraPageCount: number;
  ttlMinutes?: number;
}): Promise<{ token: string; expiresAt: string; publicUrl: string }> {
  const workspaceId = String(params.workspaceId ?? '').trim();
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  if (!isPersistableUuid(workspaceId) || !isPersistableUuid(presentationRefId)) {
    throw new Error('슬라이드 연결 정보가 올바르지 않습니다. 저장한 뒤 다시 시도해 주세요.');
  }

  await getAuthBearerToken();

  const existing = await findActivePresentationPublicSession(workspaceId, presentationRefId);
  const extraPageCount = Number.isFinite(params.extraPageCount)
    ? Math.max(1, Math.floor(params.extraPageCount))
    : 1;
  const meta = JSON.parse(JSON.stringify(params.meta ?? {
    templateLabel: '',
    unit: '',
    page: '',
    assistantPage: '',
    programContent: '',
    achievementText: '',
  })) as ProgramContentSlidePayloadV1['meta'];

  if (existing) {
    // 활성 세션 재사용 시에도 최신 쪽수·메타를 반영해야 QR 푸터 탭이 늘어난다.
    const { error: updateError } = await supabase
      .from('presentation_public_sessions')
      .update({
        meta_json: meta,
        extra_page_count: extraPageCount,
      })
      .eq('token', existing.token)
      .is('revoked_at', null);
    if (updateError && process.env.NODE_ENV === 'development') {
      console.error('[presentationPublicShare] refresh active session meta/count', updateError);
    }
    const reused = {
      ...existing,
      publicUrl: buildPublicPresentationUrl(existing.token),
    };
    writeActivePublicSessionMemoryCache(workspaceId, presentationRefId, existing);
    return reused;
  }

  const ttlMinutes = params.ttlMinutes ?? PRESENTATION_PUBLIC_SESSION_TTL_MINUTES;

  const { data, error } = await supabase
    .from('presentation_public_sessions')
    .insert({
      workspace_id: workspaceId,
      presentation_ref_id: presentationRefId,
      meta_json: meta,
      extra_page_count: extraPageCount,
      ttl_minutes: ttlMinutes,
      token: 'cp-placeholder-token-not-used',
      expires_at: '1970-01-01T00:00:00.000Z',
    })
    .select('token, expires_at')
    .single();

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[presentationPublicShare] presentation_public_sessions insert', error);
    }
    throw new Error(formatPresentationShareRpcError(error.message, undefined, error.code));
  }

  const parsed = parseCreateSessionResult(data);
  if (!parsed?.token) throw new Error('공개보기 링크를 만들지 못했습니다.');
  writeActivePublicSessionMemoryCache(workspaceId, presentationRefId, parsed);
  return { token: parsed.token, expiresAt: parsed.expiresAt, publicUrl: buildPublicPresentationUrl(parsed.token) };
}

function parsePublicPresentationPayload(
  data: {
    meta?: ProgramContentSlidePayloadV1['meta'];
    extra_page_count?: number;
    expires_at?: string;
    signature?: string;
    pages?: Array<{ page_index?: number; layout_json?: unknown; updated_at?: string | null }>;
  } | null
): { payload: PublicPresentationPayload; signature: string } | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data;
  const layouts: Record<number, ProgramSlideLayoutV1> = {};
  let maxPageIndex = -1;
  for (const page of raw.pages ?? []) {
    const pageIndex = Number(page.page_index);
    if (!Number.isFinite(pageIndex) || pageIndex < 0) continue;
    const row = {
      page_index: pageIndex,
      layout_json: page.layout_json,
      updated_at: String(page.updated_at ?? '').trim(),
    } as ProgramContentSlidePageRow;
    layouts[pageIndex] = layoutFromDbRow(row);
    if (pageIndex > maxPageIndex) maxPageIndex = pageIndex;
  }
  const sessionExtra = Math.max(1, Number(raw.extra_page_count) || 1);
  const pagesDerivedExtra = maxPageIndex >= 0 ? maxPageIndex + 1 : 0;
  const layoutKeyCount = Object.keys(layouts).length;
  return {
    signature: String(raw.signature ?? '').trim(),
    payload: {
      meta: raw.meta ?? {
        templateLabel: '',
        unit: '',
        page: '',
        assistantPage: '',
        programContent: '',
        achievementText: '',
      },
      // 세션 스냅샷이 오래돼도 실제 pages 기준으로 탭 수를 보정
      extraPageCount: Math.max(1, sessionExtra, pagesDerivedExtra, layoutKeyCount),
      expiresAt: String(raw.expires_at ?? ''),
      layouts,
    },
  };
}

async function fetchPublicPresentationSignature(token: string): Promise<{ signature: string; expiresAt: string } | null> {
  const data = await postSupabaseRpc<{ signature?: string; expires_at?: string } | null>(
    'fetch_public_presentation_signature_by_token',
    { p_token: token.trim() },
    SUPABASE_ANON_KEY
  );
  if (!data || typeof data !== 'object') return null;
  const signature = String(data.signature ?? '').trim();
  const expiresAt = String(data.expires_at ?? '').trim();
  if (!signature) return null;
  return { signature, expiresAt };
}

export interface PublicPresentationMetaPayload {
  meta: ProgramContentSlidePayloadV1['meta'];
  expiresAt: string;
}

/**
 * 질문 QR 전용: 슬라이드 페이지(layout_json)를 전혀 조회하지 않는 경량 RPC.
 * 편제/단원/쪽수/학습내용 배너만 필요할 때(질문 전용 화면) 사용해
 * 공개 QR 대비 네트워크·DB 조회량을 크게 줄인다.
 */
export async function fetchPublicPresentationMetaByToken(
  token: string
): Promise<PublicPresentationMetaPayload | null> {
  const trimmed = token.trim();
  if (!trimmed) return null;
  const data = await postSupabaseRpc<{
    meta?: ProgramContentSlidePayloadV1['meta'];
    expires_at?: string;
  } | null>('fetch_public_presentation_meta_by_token', { p_token: trimmed }, SUPABASE_ANON_KEY);
  if (!data || typeof data !== 'object') return null;
  return {
    meta: data.meta ?? {
      templateLabel: '',
      unit: '',
      page: '',
      assistantPage: '',
      programContent: '',
      achievementText: '',
    },
    expiresAt: String(data.expires_at ?? ''),
  };
}

export async function fetchPublicPresentationByToken(
  token: string,
  options?: { forceRefresh?: boolean }
): Promise<PublicPresentationPayload | null> {
  const trimmed = token.trim();
  if (!trimmed) return null;

  const cached = options?.forceRefresh ? null : readPublicPresentationCache(trimmed);
  if (cached) {
    try {
      const remoteSignature = await fetchPublicPresentationSignature(trimmed);
      if (!remoteSignature) {
        removePublicPresentationCache(trimmed);
        return null;
      }
      if (
        remoteSignature.signature === cached.signature &&
        (!remoteSignature.expiresAt || remoteSignature.expiresAt === cached.expiresAt)
      ) {
        return cached.payload;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e ?? '');
      if (!/fetch_public_presentation_signature_by_token|could not find the function|PGRST202/i.test(msg)) {
        throw e;
      }
      // 점진 배포 중(새 RPC 미적용)에는 기존 풀 fetch 경로로 폴백
    }
  }

  const data = await postSupabaseRpc<{
    meta?: ProgramContentSlidePayloadV1['meta'];
    extra_page_count?: number;
    expires_at?: string;
    signature?: string;
    pages?: Array<{ page_index?: number; layout_json?: unknown; updated_at?: string | null }>;
  } | null>('fetch_public_presentation_by_token', { p_token: trimmed }, SUPABASE_ANON_KEY);

  const parsed = parsePublicPresentationPayload(data);
  if (!parsed) {
    removePublicPresentationCache(trimmed);
    return null;
  }
  if (parsed.signature) {
    writePublicPresentationCache(trimmed, parsed.signature, parsed.payload);
  }
  return parsed.payload;
}
